/** 投放项目 Query 层：运行中的后台批次持续轮询，关闭展示不取消服务端执行。 */
import type { SystemLanguageResponse } from '@novel-admin/shared';
import { type QueryClient, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { apiRequest } from '~/api/client';
import { getMe } from '~/hooks/use-auth';
import { booksApi, chaptersApi } from '~/hooks/use-books';
import {
  type ProjectDesensitizationFilter,
  type ProjectOperation,
  type ProjectProgress,
  type ProjectQueueState,
  type ProjectSort,
  landingProjectsApi,
} from '~/hooks/use-landing-projects';
import { landingProductCopyApi, landingTemplatesApi } from '~/hooks/use-landing-templates';
export const landingProjectQueryKey = ['landing-projects'];
export function projectExecutionActive(
  execution: Pick<ProjectProgress, 'status'> | null | undefined,
) {
  return (
    !!execution &&
    ['PENDING', 'QUEUED', 'RUNNING', 'WAITING', 'WAITING_DEPENDENCY', 'RETRYING'].includes(
      execution.status,
    )
  );
}
/** 全局风险清单独立于当前项目的搜索、状态及分页，刷新失败不能显示成没有风险。 */
export function useLandingProjectBookVisibilityWarnings(page: number) {
  return useQuery({
    queryKey: [...landingProjectQueryKey, 'book-visibility-warnings', page],
    // @real — 每次按当前书组、语言版和目标 App 的覆盖层重新计算。
    queryFn: () => landingProjectsApi.bookVisibilityWarnings(page),
    staleTime: 0,
    refetchInterval: 30000,
    refetchOnWindowFocus: 'always',
  });
}
export function useLandingProjects(params: {
  page: number;
  limit: number;
  search: string;
  status?: string;
  sort?: ProjectSort;
  product_id?: string;
  language?: string;
  desensitization_status?: ProjectDesensitizationFilter;
}) {
  return useQuery({
    queryKey: [...landingProjectQueryKey, 'list', params],
    // @real — 请求服务端真实数据，不使用运行时夹具。
    queryFn: () =>
      landingProjectsApi.list(
        params.page,
        params.limit,
        params.search,
        params.status,
        params.sort,
        params.product_id,
        params.language,
        params.desensitization_status,
      ),
    // 脱敏状态筛选的每次刷新都要在服务端推导全部候选项目，进度轮询放慢到 10s。
    refetchInterval: (query) =>
      query.state.data?.data.some((row) => projectExecutionActive(row.active_execution))
        ? params.desensitization_status
          ? 10_000
          : 3000
        : false,
  });
}
/** 项目列表语言筛选候选：只读语言端点，避免地区端点失败连带清空语言选项。 */
export function useLandingProjectLanguageOptions() {
  return useQuery({
    queryKey: ['sys-config', 'languages'],
    // @real — 请求服务端真实数据，不使用运行时夹具。
    queryFn: () =>
      apiRequest<SystemLanguageResponse[]>({
        method: 'GET',
        path: '/api/v1/system-config/languages',
      }),
  });
}
export function useLandingProject(id: string) {
  return useQuery({
    queryKey: [...landingProjectQueryKey, id],
    // @real — 请求服务端真实数据，不使用运行时夹具。
    queryFn: () => landingProjectsApi.detail(id),
    enabled: !!id,
    staleTime: 0,
    refetchOnWindowFocus: 'always',
    refetchInterval: (query) =>
      projectExecutionActive(query.state.data?.active_execution) ? 3000 : 30000,
  });
}
export function useLandingProjectChapters(id: string) {
  return useQuery({
    queryKey: [...landingProjectQueryKey, id, 'chapters'],
    // @real — 请求服务端真实数据，不使用运行时夹具。
    queryFn: () => landingProjectsApi.chapters(id),
    enabled: !!id,
    refetchInterval: (query) =>
      query.state.data?.some((row) => projectExecutionActive(row.execution)) ? 3000 : false,
  });
}
/** 一键脱敏弹窗按项目各读一次章节列表；与项目详情共用缓存键，提交后统一失效。 */
export function useLandingProjectsChapterLists(ids: string[]) {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: [...landingProjectQueryKey, id, 'chapters'],
      // @real — 请求服务端真实数据，不使用运行时夹具。
      queryFn: () => landingProjectsApi.chapters(id),
      staleTime: 0,
    })),
  });
}
export function useLandingProjectChapterContent(id: string, chapterId: string) {
  return useQuery({
    queryKey: [...landingProjectQueryKey, id, 'chapter-content', chapterId],
    // @real — 加载当前项目采用的营销正文或未处理原文。
    queryFn: () => landingProjectsApi.chapterContent(id, chapterId),
    enabled: !!id && !!chapterId,
  });
}
export async function loadLandingProjectAssets(id: string) {
  const rows: Awaited<ReturnType<typeof landingProjectsApi.assets>>['data'] = [];
  for (let page = 1; ; page += 1) {
    const response = await landingProjectsApi.assets(id, page, 200);
    rows.push(...response.data);
    if (!response.hasMore) return rows;
  }
}
/**
 * 定位落地页任务在「HTML 落地页」列表（每页 pageSize 条）中的页码；按与列表相同的服务端排序
 * 每批 200 条扫描，找不到返回 null。素材卡片跳转用，避免只打开第 1 页导致目标不在视野内。
 */
export async function locateLandingProjectTaskPage(id: string, taskId: string, pageSize = 20) {
  for (let batch = 1; ; batch += 1) {
    const response = await landingProjectsApi.tasks(id, batch, 200);
    const index = response.data.findIndex((task) => task.id === taskId);
    if (index >= 0) return Math.floor(((batch - 1) * 200 + index) / pageSize) + 1;
    if (!response.hasMore || !response.data.length) return null;
  }
}
export function useLandingProjectAvailableChapters(
  id: string,
  page: number,
  search: string,
  availability: 'free' | 'paid' | 'all' = 'all',
  enabled = true,
) {
  return useQuery({
    queryKey: [...landingProjectQueryKey, id, 'available-chapters', page, search, availability],
    // @real — 请求服务端真实数据，不使用运行时夹具。
    queryFn: () => landingProjectsApi.availableChapters(id, page, 50, search, availability),
    enabled: !!id && enabled,
  });
}
/** 工作台展示整本书，逐页读取候选；项目已采用版本与执行状态仍由 chapters 接口提供。 */
export function useLandingProjectChapterCatalog(id: string) {
  return useQuery({
    queryKey: [...landingProjectQueryKey, id, 'chapter-catalog'],
    // @real — 不用已选章节或候选首页代替全书章节集合。
    queryFn: async () => {
      const rows: Awaited<ReturnType<typeof landingProjectsApi.availableChapters>>['data'] = [];
      for (let page = 1; ; page += 1) {
        const response = await landingProjectsApi.availableChapters(id, page, 200, '', 'all');
        rows.push(...response.data);
        if (!response.hasMore) return rows;
      }
    },
    enabled: !!id,
  });
}
export function useLandingProjectAssets(id: string, page: number) {
  return useQuery({
    queryKey: [...landingProjectQueryKey, id, 'assets', page],
    // @real — 请求服务端真实数据，不使用运行时夹具。
    queryFn: () => landingProjectsApi.assets(id, page, 20),
    enabled: !!id,
  });
}
const landingProjectTasksQuery = (id: string, page: number) => ({
  queryKey: [...landingProjectQueryKey, id, 'tasks', page],
  // @real — 请求服务端真实数据，不使用运行时夹具。
  queryFn: () => landingProjectsApi.tasks(id, page, 20),
});
/**
 * 详情首屏与项目详情并行预取常用 Tab 数据，避免「详情返回后才开始拉任务 / 模板」的串行瀑布。
 * 只预取不订阅：不跟随任务轮询，进入对应 Tab 时直接命中缓存。
 */
export function prefetchLandingProjectWorkbench(client: QueryClient, id: string) {
  void client.prefetchQuery(landingProjectTasksQuery(id, 1));
  void client.prefetchQuery({
    queryKey: ['landing-templates'],
    queryFn: () => landingTemplatesApi.get(),
    retry: false,
  });
}
export function useLandingProjectTasks(id: string, page: number) {
  return useQuery({
    ...landingProjectTasksQuery(id, page),
    enabled: !!id,
    refetchInterval: (query) =>
      query.state.data?.data.some((row) => projectExecutionActive(row.execution)) ? 3000 : false,
  });
}
/**
 * 项目执行记录。传 `reconcile` 的实例负责在执行结束时刷新项目、章节与任务——同一页只应有一个实例传它
 * （详情页），否则每个实例各刷新一遍。
 */
export function useLandingProjectExecutions(
  id: string,
  page = 1,
  reconcile?: { detailUpdatedAt: number },
) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: [...landingProjectQueryKey, id, 'executions', page],
    // @real — 请求服务端真实数据，不使用运行时夹具。
    queryFn: () => landingProjectsApi.executions(id, page, 20),
    enabled: !!id,
    refetchInterval: (query) =>
      query.state.data?.data.some(projectExecutionActive) ? 3000 : false,
  });
  const latestId = query.data?.data[0]?.id;
  const latestStatus = query.data?.data[0]?.status;
  const latestFinishedAt = query.data?.data[0]?.finished_at ?? null;
  const reconciling = reconcile !== undefined;
  const detailUpdatedAt = reconcile?.detailUpdatedAt ?? 0;
  // 观察到执行从运行中转为结束时刷新项目。首次看到的已结束执行，只在它可能晚于详情数据结束时刷新
  // （与详情并发加载时恰好收尾）；早已结束的历史执行不重复拉详情。详情尚未到手时先不判定。
  const observed = useRef<{ id: string; active: boolean } | null>(null);
  useEffect(() => {
    if (!reconciling || !latestId || !detailUpdatedAt) return;
    const previous = observed.current;
    const active = !['SUCCEEDED', 'PARTIAL', 'FAILED', 'CANCELLED'].includes(latestStatus ?? '');
    observed.current = { id: latestId, active };
    if (active || (previous && previous.id === latestId && !previous.active)) return;
    if (!previous) {
      const finishedAt = latestFinishedAt ? Date.parse(latestFinishedAt) : Number.NaN;
      // 留 60s 余量吸收客户端与服务端时钟偏差；判不准时宁可多刷新一次。
      if (Number.isFinite(finishedAt) && finishedAt < detailUpdatedAt - 60_000) return;
    }
    void client.invalidateQueries({ queryKey: [...landingProjectQueryKey, id], exact: true });
    void client.invalidateQueries({ queryKey: [...landingProjectQueryKey, id, 'chapters'] });
    void client.invalidateQueries({ queryKey: [...landingProjectQueryKey, id, 'tasks'] });
    // 素材卡片展示关联落地页的生成状态，执行结束同样需要刷新。
    void client.invalidateQueries({ queryKey: [...landingProjectQueryKey, id, 'assets'] });
  }, [reconciling, id, latestId, latestStatus, latestFinishedAt, detailUpdatedAt, client]);
  return query;
}
export function useLandingProjectExecution(id: string, executionId: string | null) {
  return useQuery({
    queryKey: [...landingProjectQueryKey, id, 'execution', executionId],
    // @real — 请求服务端真实数据，不使用运行时夹具。
    queryFn: () => landingProjectsApi.execution(id, executionId ?? ''),
    enabled: !!id && !!executionId,
    refetchInterval: (query) => (projectExecutionActive(query.state.data) ? 3000 : false),
  });
}
/**
 * 跨项目执行队列一页；有进行中批次时 3s 轮询。「进行中」视图即使为空也以 10s 兜底轮询，
 * 才能看到其他运营新提交的批次；关闭抽屉即停止订阅。
 */
export function useLandingProjectExecutionQueue(params: {
  page: number;
  state: ProjectQueueState;
  operation?: ProjectOperation;
  enabled: boolean;
}) {
  const { enabled, ...key } = params;
  return useQuery({
    queryKey: [...landingProjectQueryKey, 'execution-queue', key],
    // @real — GET /api/v1/landing-projects/executions
    queryFn: () =>
      landingProjectsApi.executionQueue(params.page, 20, params.state, params.operation),
    enabled,
    refetchInterval: (query) =>
      query.state.data?.data.some(projectExecutionActive)
        ? 3000
        : params.state === 'active'
          ? 10000
          : false,
  });
}
/** 入口角标只取进行中总数：有排队时 5s 刷新，空闲时 30s 兜底发现其他运营新提交的任务。 */
export function useLandingProjectExecutionQueueCount() {
  return useQuery({
    queryKey: [...landingProjectQueryKey, 'execution-queue', 'active-count'],
    // @real — 同一总览接口 limit=1，只读 total。
    queryFn: async () => (await landingProjectsApi.executionQueue(1, 1, 'active')).total,
    refetchInterval: (query) => ((query.state.data ?? 0) > 0 ? 5000 : 30000),
  });
}
export function useLandingChapterHistory(id: string, chapterId: string, page: number) {
  return useQuery({
    queryKey: [...landingProjectQueryKey, id, 'history', chapterId, page],
    // @real — 请求服务端真实数据，不使用运行时夹具。
    queryFn: () => landingProjectsApi.history(id, chapterId, page, 20),
    enabled: !!chapterId,
  });
}
export function useLandingChapterHistoryDetail(
  id: string,
  chapterId: string,
  versionId: string | null,
) {
  return useQuery({
    queryKey: [...landingProjectQueryKey, id, 'history-detail', chapterId, versionId],
    // @real — 请求服务端真实数据，不使用运行时夹具。
    queryFn: () => landingProjectsApi.historyDetail(id, chapterId, versionId ?? ''),
    enabled: !!versionId,
  });
}

export function useLandingProjectBook(novelId: string) {
  return useQuery({
    queryKey: ['books', 'landing-project-book', novelId],
    // @real — 请求服务端真实数据，不使用运行时夹具。
    queryFn: () => booksApi.language(novelId),
    enabled: !!novelId,
  });
}
export function useLandingProjectBookChapters(novelId: string) {
  return useQuery({
    queryKey: ['chapters', 'landing-project-create', novelId],
    // @real — 请求服务端真实数据，不使用运行时夹具。
    queryFn: () => chaptersApi.list(novelId),
    enabled: !!novelId,
  });
}

/** 全选筛选结果走真实分页；保留实际选中 ID，不将“全选”解释为隐藏的未来章节。 */
export async function loadLandingSelectableChapters(
  id: string,
  search: string,
  availability: 'free' | 'paid' | 'all' = 'all',
) {
  const rows: Awaited<ReturnType<typeof landingProjectsApi.availableChapters>>['data'] = [];
  for (let page = 1; ; page += 1) {
    const response = await landingProjectsApi.availableChapters(
      id,
      page,
      200,
      search,
      availability,
    );
    rows.push(...response.data.filter((row) => row.selectable && !row.selected));
    if (rows.length > 1000) throw new Error('单个项目最多选择 1000 章，请缩小筛选范围');
    if (!response.hasMore) return rows;
  }
}

/** 默认文案由产品配置优先、生成器同语言默认回退，保存时冻结完整值。 */
export function useLandingProjectDefaultCopy(productId: string, locale: string) {
  return useQuery({
    queryKey: ['landing-copy', productId, locale],
    // @real — 请求服务端真实数据，不使用运行时夹具。
    queryFn: () => landingProductCopyApi.get(productId, locale),
    enabled: !!productId && !!locale,
  });
}

/** 复制项目选择新产品时，按服务端过滤结果重新校验同一本语言行，避免跨 App 沿用旧资格。 */
export function useLandingProjectBookEligibility(
  productId: string,
  novel: { id: string; book_code: string; locale: string },
) {
  return useQuery({
    queryKey: [...landingProjectQueryKey, 'book-eligibility', productId, novel.id],
    // @real — 可阅读编码精确定位同书语言行，资格由产品下的三层可见性过滤决定。
    queryFn: async () => {
      for (let page = 1; ; page += 1) {
        const response = await booksApi.listOffsetPaged(page, 200, {
          q: novel.book_code,
          lang: [novel.locale],
          landing_product_id: productId,
        });
        if (response.data.some((group) => group.languages.some((row) => row.novel_id === novel.id)))
          return true;
        if (!response.hasMore) return false;
      }
    },
    enabled: !!productId,
  });
}

/**
 * 当前登录账号的 CODE（创意组名 BY 段）。登录态里的 user 是登录 / 刷新时的快照，
 * 超管之后补设的 CODE 不会出现，故这里直接读 `/auth/me`。
 */
export function useCurrentAdminAccountCode() {
  return useQuery({
    queryKey: ['auth', 'me', 'account-code'],
    // @real — GET /api/v1/auth/me
    queryFn: async () => (await getMe()).account_code ?? null,
  });
}
